<template>
  <header v-if="isAmoSite" class="ll-pub-hdr ll-amo-header" :style="amoHeaderStyle">
    <div class="ll-amo-top">
      <button
        type="button"
        class="ll-amo-icon-btn ll-amo-menu-toggle"
        :style="amoIconButtonStyle"
        @click.stop="toggleMobileMenu"
        :aria-label="mobileMenuOpen ? $t('layouts.login.nav-menu-close') : $t('layouts.login.nav-menu-open')"
      >
        <i :class="mobileMenuOpen ? 'fas fa-times' : 'fas fa-bars'" class="ll-amo-icon" aria-hidden="true"></i>
      </button>

      <a :href="logoHomeHref" class="ll-amo-logo-link" :title="$t('layouts.login.heading')">
        <img :src="amoLogoUrl" alt="AMO Racing" class="ll-amo-logo-img" @error="onAmoLogoError" />
      </a>

      <div class="ll-amo-lang" :style="amoIconButtonStyle">
        <LanguageSwitcher />
      </div>
    </div>

    <nav v-if="hasStrapiMenu" class="ll-amo-menu-row" :style="amoMenuRowStyle" :aria-label="$t('layouts.login.public-nav-aria')">
      <template v-for="item in strapiMenu">
        <a
          v-if="item.children.length === 0"
          :key="`amo-link-${item.id}`"
          :href="item.href"
          :target="item.openInNewTab ? '_blank' : null"
          :rel="item.openInNewTab ? 'noopener noreferrer' : null"
          class="ll-amo-menu-item"
          :style="amoMenuButtonStyle"
        >
          {{ item.name }}
        </a>
        <div v-else :key="`amo-menu-${item.id}`" class="ll-amo-dd">
          <button
            type="button"
            class="ll-amo-menu-item ll-amo-menu-parent"
            :style="amoMenuButtonStyle"
            @click.stop="toggleDesktopGroup(desktopGroupId(item))"
          >
            {{ item.name }}
            <i class="fa fa-caret-square-down ll-amo-caret" aria-hidden="true"></i>
          </button>
          <div v-if="openDesktopGroup === desktopGroupId(item)" class="ll-amo-dd-menu" :style="amoDropDownStyle">
            <a
              v-for="child in item.children"
              :key="`amo-sub-${child.id}`"
              :href="child.href"
              :target="child.openInNewTab ? '_blank' : null"
              :rel="child.openInNewTab ? 'noopener noreferrer' : null"
              class="ll-amo-dd-item"
              :style="amoDropDownItemStyle"
            >
              {{ child.name }}
            </a>
          </div>
        </div>
      </template>
    </nav>

    <div v-if="mobileMenuOpen" class="ll-mob-overlay ll-amo-overlay" @click="closeMobileMenu"></div>
    <transition name="ll-slide">
      <div v-if="mobileMenuOpen" class="ll-mob-drawer ll-amo-drawer">
        <div class="ll-mob-drawer-inner">
          <template v-if="hasStrapiMenu">
            <template v-for="item in strapiMenu">
              <a
                v-if="item.children.length === 0"
                :key="`amo-mobile-link-${item.id}`"
                :href="item.href"
                :target="item.openInNewTab ? '_blank' : null"
                :rel="item.openInNewTab ? 'noopener noreferrer' : null"
                class="ll-mob-link"
                @click="closeMobileMenu"
              >
                {{ item.name }}
              </a>
              <div v-else :key="`amo-mobile-menu-${item.id}`" class="ll-mob-group">
                <button type="button" class="ll-mob-link ll-mob-toggle" @click="toggleGroup(item.id)">
                  <span class="ll-mob-toggle-left">{{ item.name }}</span>
                  <i :class="openGroup === item.id ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="ll-mob-chevron" aria-hidden="true"></i>
                </button>
                <div v-if="openGroup === item.id" class="ll-mob-sub">
                  <a
                    v-for="child in item.children"
                    :key="`amo-mobile-sub-${child.id}`"
                    :href="child.href"
                    :target="child.openInNewTab ? '_blank' : null"
                    :rel="child.openInNewTab ? 'noopener noreferrer' : null"
                    class="ll-mob-sublink"
                    @click="closeMobileMenu"
                  >
                    {{ child.name }}
                  </a>
                </div>
              </div>
            </template>
          </template>
          <template v-else>
            <router-link :to="loginRoute" class="ll-mob-link" @click.native="closeMobileMenu">{{ $t('layouts.login.nav-login-participant') }}</router-link>
          </template>

          <div class="ll-mob-group ll-mob-lang-group">
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </transition>
  </header>

  <header v-else class="ll-pub-hdr" :style="{ backgroundColor: headerBg }">
    <div v-show="!isMobile" class="ll-desk">
      <a :href="logoHomeHref" class="ll-logo-link" :title="$t('layouts.login.heading')">
        <img :src="logoUrl" alt="LiveLaps" class="ll-logo-img" @error="onLogoError" />
      </a>

      <nav v-if="envReady && showFullNav" class="ll-desk-nav" :aria-label="$t('layouts.login.public-nav-aria')">
        <template v-if="hasStrapiMenu">
          <template v-for="item in strapiMenu">
            <a
              v-if="item.children.length === 0"
              :key="`desktop-link-${item.id}`"
              :href="item.href"
              :target="item.openInNewTab ? '_blank' : null"
              :rel="item.openInNewTab ? 'noopener noreferrer' : null"
              class="ll-desk-btn"
            >
              {{ item.name }}
            </a>
            <div v-else :key="`desktop-menu-${item.id}`" class="ll-desk-dd">
              <button type="button" class="ll-desk-btn" @click.stop="toggleDesktopGroup(desktopGroupId(item))">
                {{ item.name }}
                <i class="fa fa-caret-down ll-caret" aria-hidden="true"></i>
              </button>
              <div v-if="openDesktopGroup === desktopGroupId(item)" class="ll-desk-dd-menu">
                <a
                  v-for="child in item.children"
                  :key="`desktop-sub-${child.id}`"
                  :href="child.href"
                  :target="child.openInNewTab ? '_blank' : null"
                  :rel="child.openInNewTab ? 'noopener noreferrer' : null"
                  class="ll-desk-dd-item"
                >
                  {{ child.name }}
                </a>
              </div>
            </div>
          </template>
        </template>

        <template v-else>
          <a :href="urlRealtime" class="ll-desk-btn">
            <i class="fa fa-cubes fa-lg ll-fa" aria-hidden="true"></i>
            {{ $t('layouts.login.nav-realtime-events') }}
          </a>
          <a :href="urlRaceResults" class="ll-desk-btn">
            <i class="fas fa-poll fa-lg ll-fa" aria-hidden="true"></i>
            {{ $t('layouts.login.nav-race-results') }}
          </a>

          <div class="ll-desk-dd">
            <button type="button" class="ll-desk-btn" @click.stop="toggleDesktopGroup('discover')">
              <i class="fa fa-child fa-lg ll-fa" aria-hidden="true"></i>
              {{ $t('layouts.login.nav-discover') }}
              <i class="fa fa-caret-down ll-caret" aria-hidden="true"></i>
            </button>
            <div v-if="openDesktopGroup === 'discover'" class="ll-desk-dd-menu">
              <a :href="marketingUrl('/stories')" class="ll-desk-dd-item">
                <i class="fa fa-book ll-dd-icon" aria-hidden="true"></i>
                {{ $t('layouts.login.nav-discover-stories') }}
              </a>
              <a :href="marketingUrl('/videos')" class="ll-desk-dd-item">
                <i class="fa fa-video ll-dd-icon" aria-hidden="true"></i>
                {{ $t('layouts.login.nav-discover-videos') }}
              </a>
              <a :href="marketingUrl('/contact-us')" class="ll-desk-dd-item">
                <i class="fa fa-envelope ll-dd-icon" aria-hidden="true"></i>
                {{ $t('layouts.login.nav-discover-contact') }}
              </a>
            </div>
          </div>

          <div class="ll-desk-dd">
            <button type="button" class="ll-desk-btn" @click.stop="toggleDesktopGroup('learn')">
              <i class="fa fa-graduation-cap fa-lg ll-fa" aria-hidden="true"></i>
              {{ $t('layouts.login.nav-learn') }}
              <i class="fa fa-caret-down ll-caret" aria-hidden="true"></i>
            </button>
            <div v-if="openDesktopGroup === 'learn'" class="ll-desk-dd-menu">
              <a :href="marketingUrl('/about')" class="ll-desk-dd-item">{{ $t('layouts.login.nav-learn-about') }}</a>
              <a :href="marketingUrl('/cellScore')" class="ll-desk-dd-item">{{ $t('layouts.login.nav-learn-touch') }}</a>
              <a :href="marketingUrl('/registration')" class="ll-desk-dd-item">{{ $t('layouts.login.nav-learn-registration') }}</a>
            </div>
          </div>

          <div class="ll-desk-dd">
            <button type="button" class="ll-desk-btn" @click.stop="toggleDesktopGroup('create')">
              <i class="fa fa-users fa-lg ll-fa" aria-hidden="true"></i>
              {{ $t('layouts.login.nav-marketing-create-account') }}
              <i class="fa fa-caret-down ll-caret" aria-hidden="true"></i>
            </button>
            <div v-if="openDesktopGroup === 'create'" class="ll-desk-dd-menu">
              <a :href="urlCreateAccount" class="ll-desk-dd-item">{{ $t('layouts.login.nav-create-participant') }}</a>
              <a :href="marketingUrl('/contact-account-creation.php')" class="ll-desk-dd-item">{{ $t('layouts.login.nav-create-promoter') }}</a>
            </div>
          </div>

          <div class="ll-desk-dd">
            <button type="button" class="ll-desk-btn" @click.stop="toggleDesktopGroup('login')">
              <i class="fas fa-sign-in-alt fa-lg ll-fa" aria-hidden="true"></i>
              {{ $t('layouts.login.nav-log-in') }}
              <i class="fa fa-caret-down ll-caret" aria-hidden="true"></i>
            </button>
            <div v-if="openDesktopGroup === 'login'" class="ll-desk-dd-menu">
              <router-link :to="loginRoute" class="ll-desk-dd-item">{{ $t('layouts.login.nav-login-participant') }}</router-link>
              <a :href="urlPromoterLogin" class="ll-desk-dd-item">{{ $t('layouts.login.nav-login-promoter') }}</a>
            </div>
          </div>
        </template>
      </nav>

      <div class="ll-desk-lang">
        <LanguageSwitcher />
      </div>
    </div>

    <div v-show="isMobile" class="ll-mob">
      <div class="ll-mob-topbar">
        <a :href="logoHomeHref" class="ll-logo-link" :title="$t('layouts.login.heading')">
          <img :src="logoUrl" alt="LiveLaps" class="ll-logo-img" @error="onLogoError" />
        </a>
        <div v-if="!showFullNav" class="ll-mob-lang-inline"><LanguageSwitcher /></div>
        <button
          v-if="showFullNav"
          type="button"
          class="ll-hamburger"
          @click.stop="toggleMobileMenu"
          :aria-label="mobileMenuOpen ? $t('layouts.login.nav-menu-close') : $t('layouts.login.nav-menu-open')"
        >
          <i :class="mobileMenuOpen ? 'fas fa-times' : 'fas fa-bars'" class="ll-hamburger-icon" aria-hidden="true"></i>
        </button>
      </div>

      <div v-if="mobileMenuOpen && showFullNav" class="ll-mob-overlay" @click="closeMobileMenu"></div>
      <transition name="ll-slide">
        <div v-if="mobileMenuOpen && showFullNav" class="ll-mob-drawer">
          <div v-if="envReady" class="ll-mob-drawer-inner">
            <template v-if="hasStrapiMenu">
              <template v-for="item in strapiMenu">
                <a
                  v-if="item.children.length === 0"
                  :key="`mobile-link-${item.id}`"
                  :href="item.href"
                  :target="item.openInNewTab ? '_blank' : null"
                  :rel="item.openInNewTab ? 'noopener noreferrer' : null"
                  class="ll-mob-link"
                  @click="closeMobileMenu"
                >
                  {{ item.name }}
                </a>
                <div v-else :key="`mobile-menu-${item.id}`" class="ll-mob-group">
                  <button type="button" class="ll-mob-link ll-mob-toggle" @click="toggleGroup(item.id)">
                    <span class="ll-mob-toggle-left">{{ item.name }}</span>
                    <i :class="openGroup === item.id ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="ll-mob-chevron" aria-hidden="true"></i>
                  </button>
                  <div v-if="openGroup === item.id" class="ll-mob-sub">
                    <a
                      v-for="child in item.children"
                      :key="`mobile-sub-${child.id}`"
                      :href="child.href"
                      :target="child.openInNewTab ? '_blank' : null"
                      :rel="child.openInNewTab ? 'noopener noreferrer' : null"
                      class="ll-mob-sublink"
                      @click="closeMobileMenu"
                    >
                      {{ child.name }}
                    </a>
                  </div>
                </div>
              </template>
            </template>

            <template v-else>
              <a :href="urlRealtime" class="ll-mob-link" @click="closeMobileMenu">
                <i class="fa fa-cubes ll-mob-fa" aria-hidden="true"></i>
                {{ $t('layouts.login.nav-realtime-events') }}
              </a>

              <a :href="urlRaceResults" class="ll-mob-link" @click="closeMobileMenu">
                <i class="fas fa-poll ll-mob-fa" aria-hidden="true"></i>
                {{ $t('layouts.login.nav-race-results') }}
              </a>

              <div class="ll-mob-group">
                <button type="button" class="ll-mob-link ll-mob-toggle" @click="toggleGroup('discover')">
                  <span class="ll-mob-toggle-left">
                    <i class="fa fa-child ll-mob-fa" aria-hidden="true"></i>
                    {{ $t('layouts.login.nav-discover') }}
                  </span>
                  <i :class="openGroup === 'discover' ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="ll-mob-chevron" aria-hidden="true"></i>
                </button>
                <div v-if="openGroup === 'discover'" class="ll-mob-sub">
                  <a :href="marketingUrl('/stories')" class="ll-mob-sublink" @click="closeMobileMenu">{{ $t('layouts.login.nav-discover-stories') }}</a>
                  <a :href="marketingUrl('/videos')" class="ll-mob-sublink" @click="closeMobileMenu">{{ $t('layouts.login.nav-discover-videos') }}</a>
                  <a :href="marketingUrl('/contact-us')" class="ll-mob-sublink" @click="closeMobileMenu">{{ $t('layouts.login.nav-discover-contact') }}</a>
                </div>
              </div>

              <div class="ll-mob-group">
                <button type="button" class="ll-mob-link ll-mob-toggle" @click="toggleGroup('learn')">
                  <span class="ll-mob-toggle-left">
                    <i class="fa fa-graduation-cap ll-mob-fa" aria-hidden="true"></i>
                    {{ $t('layouts.login.nav-learn') }}
                  </span>
                  <i :class="openGroup === 'learn' ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="ll-mob-chevron" aria-hidden="true"></i>
                </button>
                <div v-if="openGroup === 'learn'" class="ll-mob-sub">
                  <a :href="marketingUrl('/about')" class="ll-mob-sublink" @click="closeMobileMenu">{{ $t('layouts.login.nav-learn-about') }}</a>
                  <a :href="marketingUrl('/cellScore')" class="ll-mob-sublink" @click="closeMobileMenu">{{ $t('layouts.login.nav-learn-touch') }}</a>
                  <a :href="marketingUrl('/registration')" class="ll-mob-sublink" @click="closeMobileMenu">{{ $t('layouts.login.nav-learn-registration') }}</a>
                </div>
              </div>

              <div class="ll-mob-group">
                <button type="button" class="ll-mob-link ll-mob-toggle" @click="toggleGroup('create')">
                  <span class="ll-mob-toggle-left">
                    <i class="fa fa-users ll-mob-fa" aria-hidden="true"></i>
                    {{ $t('layouts.login.nav-marketing-create-account') }}
                  </span>
                  <i :class="openGroup === 'create' ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="ll-mob-chevron" aria-hidden="true"></i>
                </button>
                <div v-if="openGroup === 'create'" class="ll-mob-sub">
                  <a :href="urlCreateAccount" class="ll-mob-sublink" @click="closeMobileMenu">{{ $t('layouts.login.nav-create-participant') }}</a>
                  <a :href="marketingUrl('/contact-account-creation.php')" class="ll-mob-sublink" @click="closeMobileMenu">{{ $t('layouts.login.nav-create-promoter') }}</a>
                </div>
              </div>

              <div class="ll-mob-group">
                <button type="button" class="ll-mob-link ll-mob-toggle" @click="toggleGroup('login')">
                  <span class="ll-mob-toggle-left">
                    <i class="fas fa-sign-in-alt ll-mob-fa" aria-hidden="true"></i>
                    {{ $t('layouts.login.nav-log-in') }}
                  </span>
                  <i :class="openGroup === 'login' ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="ll-mob-chevron" aria-hidden="true"></i>
                </button>
                <div v-if="openGroup === 'login'" class="ll-mob-sub">
                  <router-link :to="loginRoute" class="ll-mob-sublink" @click.native="closeMobileMenu">{{ $t('layouts.login.nav-login-participant') }}</router-link>
                  <a :href="urlPromoterLogin" class="ll-mob-sublink" @click="closeMobileMenu">{{ $t('layouts.login.nav-login-promoter') }}</a>
                </div>
              </div>
            </template>

            <div class="ll-mob-group ll-mob-lang-group">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </transition>
    </div>
  </header>
</template>

<script>
import axios from 'axios';
import LanguageSwitcher from '@/components/layouts/LanguageSwitcher';
import defaultLogo from '@/assets/images/mainLogo.png';
import { mapGetters } from 'vuex';

const MOBILE_BP = 991;

function joinBase(base, path) {
  const b = String(base || '').replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${b}${p}`;
}

export default {
  name: 'LivelapsPublicHeader',
  components: { LanguageSwitcher },
  data() {
    return {
      envReady: false,
      marketingRoot: '',
      livelapsHost: '',
      headerBg: '#45615F',
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : MOBILE_BP + 1,
      mobileMenuOpen: false,
      openGroup: null,
      openDesktopGroup: null,
      strapiMenu: [],
      amoTheme: {
        navBarColor: '#96c75d',
        mainMenuButtonColor: '#a9d37c',
        mainMenuButtonBorderColor: '#6c9a3a',
        mainMenuButtonFontColor: '#1f2a14',
        mainMenuButtonFontSize: '10px',
        topPageBannerLogo: ''
      }
    };
  },
  computed: {
    ...mapGetters({
      storeLogoUrl: 'vueAppLogoView',
      storeStrapiBaseUrl: 'strapiBaseUrl',
      storeSitesAppRoot: 'sitesAppRoot'
    }),
    minimal() {
      if (typeof window === 'undefined') return false;
      const host = window.location.hostname;
      const fullNavHosts = ['preprod.livelaps.com', 'labs.livelaps.com', 'www.livelaps.com', 'livelaps.com'];
      return !fullNavHosts.includes(host);
    },
    isAmoSite() {
      if (typeof window === 'undefined') return false;
      const firstHostSegment = window.location.hostname.split('.')[0].toLowerCase();
      return firstHostSegment === 'amo';
    },
    hasStrapiMenu() { return this.isAmoSite && this.strapiMenu.length > 0; },
    showFullNav() { return this.hasStrapiMenu || !this.minimal; },
    isMobile() { return this.windowWidth <= MOBILE_BP; },
    logoUrl() { return this.storeLogoUrl || defaultLogo; },
    amoLogoUrl() { return this.amoTheme.topPageBannerLogo || this.logoUrl; },
    amoResolvedFontSize() { return this.normalizeCssSize(this.amoTheme.mainMenuButtonFontSize, '10px'); },
    amoHeaderStyle() {
      return {
        backgroundColor: this.amoTheme.navBarColor
      };
    },
    amoMenuRowStyle() {
      return {
        backgroundColor: this.amoTheme.navBarColor,
        '--amo-menu-button-height': this.amoMenuButtonHeight
      };
    },
    amoIconButtonStyle() {
      return {
        backgroundColor: this.amoTheme.mainMenuButtonColor,
        borderColor: this.amoTheme.mainMenuButtonBorderColor,
        color: this.amoTheme.mainMenuButtonFontColor
      };
    },
    amoMenuButtonStyle() {
      return {
        backgroundColor: this.amoTheme.mainMenuButtonColor,
        borderColor: this.amoTheme.mainMenuButtonBorderColor,
        color: this.amoTheme.mainMenuButtonFontColor,
        fontSize: this.amoResolvedFontSize
      };
    },
    amoDropDownStyle() {
      return {
        backgroundColor: this.amoTheme.mainMenuButtonColor
      };
    },
    amoDropDownItemStyle() {
      return {
        backgroundColor: this.amoTheme.mainMenuButtonColor,
        borderColor: this.amoTheme.mainMenuButtonBorderColor,
        color: this.amoTheme.mainMenuButtonFontColor,
        fontSize: this.amoResolvedFontSize
      };
    },
    amoMenuButtonHeight() {
      const fontSizePx = this.resolveFontSizePx(this.amoResolvedFontSize);
      if (fontSizePx <= 12) return '30px';
      if (fontSizePx <= 16) return '42px';
      return '48px';
    },
    logoHomeHref() {
      if (this.marketingRoot) return joinBase(this.marketingRoot, '/');
      if (typeof window !== 'undefined') return window.location.origin + '/';
      return '/';
    },
    loginRoute() { return { name: 'Login', query: { ...this.$route.query } }; },
    strapiLocale() {
      const locale = ((this.$i18n && this.$i18n.locale) || 'en').toLowerCase();
      return locale === 'fr' ? 'fr-ca' : locale;
    },
    urlRealtime() { return joinBase(this.marketingRoot, '/promoters'); },
    urlRaceResults() { return joinBase(this.marketingRoot, '/livelaps/results'); },
    urlCreateAccount() {
      if (!this.livelapsHost) return joinBase(this.marketingRoot, '/admin/participantRegistration');
      return String(this.livelapsHost).replace(/\/$/, '') + '/registration/participant';
    },
    urlPromoterLogin() { return joinBase(this.marketingRoot, '/login'); }
  },
  watch: {
    $route() { this.closeMobileMenu(); this.closeDesktopGroup(); },
    isMobile(v) { if (!v) this.closeMobileMenu(); else this.closeDesktopGroup(); },
    '$i18n.locale'() {
      if (this.isAmoSite) {
        this.loadStrapiMenu();
      }
    }
  },
  mounted() {
    this._onResize = () => { this.windowWidth = window.innerWidth; };
    window.addEventListener('resize', this._onResize, { passive: true });
    this._onDocClick = () => { this.openDesktopGroup = null; };
    document.addEventListener('click', this._onDocClick);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('click', this._onDocClick);
  },
  methods: {
    marketingUrl(p) { return joinBase(this.marketingRoot, p); },
    onLogoError(e) { e.target.src = defaultLogo; },
    onAmoLogoError(e) {
      const fallback = this.logoUrl || defaultLogo;
      e.target.src = e.target.src === fallback ? defaultLogo : fallback;
    },
    desktopGroupId(item) { return `strapi-${item.id}`; },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
      this.openGroup = null;
      document.body.style.overflow = this.mobileMenuOpen ? 'hidden' : '';
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
      this.openGroup = null;
      document.body.style.overflow = '';
    },
    toggleGroup(id) { this.openGroup = this.openGroup === id ? null : id; },
    toggleDesktopGroup(id) { this.openDesktopGroup = this.openDesktopGroup === id ? null : id; },
    closeDesktopGroup() { this.openDesktopGroup = null; },
    async waitForEnv() {
      await this.$store.getters.envPromise;
      return new Promise((resolve) => {
        let attempts = 0;
        const readEnv = () => {
          const env = this.$store.getters.env;
          if ((env && env.size > 0) || attempts >= 20) {
            resolve(env || new Map());
            return;
          }
          attempts += 1;
          setTimeout(readEnv, 100);
        };
        readEnv();
      });
    },
    inferStrapiBaseUrl() {
      if (typeof window === 'undefined') return '';
      const parts = window.location.hostname.split('.');
      if (parts.length === 2) return `${window.location.protocol}//strapi.${window.location.hostname}`;
      if (parts.length >= 3) return `${window.location.protocol}//${parts[0]}.strapi.${parts[1]}.${parts[2]}`;
      return '';
    },
    getStrapiBaseUrl() {
      const env = this.$store.getters.env;
      const envStrapi = env && env.get ? env.get('VUE_APP_STRAPI') : '';
      return this.storeStrapiBaseUrl || envStrapi || this.inferStrapiBaseUrl();
    },
    resolveStrapiAssetUrl(base, path) {
      const value = String(path || '').trim();
      if (!value) return '';
      if (/^https?:\/\//i.test(value)) return value;
      return `${base}${value.startsWith('/') ? '' : '/'}${value}`;
    },
    normalizeCssSize(value, fallback) {
      if (value === null || value === undefined || value === '') return fallback;
      const normalized = String(value).trim().replace(/\s+/g, '');
      if (!normalized) return fallback;
      if (/^[\d.]+$/.test(normalized)) return `${normalized}px`;
      if (/^[\d.]+(px|rem|em|%)$/i.test(normalized)) return normalized;
      return fallback;
    },
    resolveFontSizePx(fontSizeValue) {
      if (!fontSizeValue) return 14;
      const normalized = String(fontSizeValue).trim().toLowerCase();
      const numericValue = parseFloat(normalized);
      if (Number.isNaN(numericValue)) return 14;
      if (normalized.endsWith('rem') || normalized.endsWith('em')) return numericValue * 16;
      if (normalized.endsWith('%')) return (numericValue / 100) * 16;
      return numericValue;
    },
    async loadAmoTheme() {
      if (!this.isAmoSite) return;

      const strapiBaseUrl = this.getStrapiBaseUrl();
      if (!strapiBaseUrl) return;

      const base = String(strapiBaseUrl).replace(/\/$/, '');

      try {
        const response = await axios.get(`${base}/api/parameters?pagination[pageSize]=1000`);
        const rawParameters = response && response.data && Array.isArray(response.data.data) ? response.data.data : [];
        const parameters = rawParameters.reduce((carry, item) => {
          const attributes = item && item.attributes ? item.attributes : {};
          if (attributes.Name) carry[attributes.Name] = attributes.Value;
          return carry;
        }, {});

        this.amoTheme = Object.assign({}, this.amoTheme, {
          navBarColor: parameters.NAVBAR_COLOR || this.amoTheme.navBarColor,
          mainMenuButtonColor: parameters.MAIN_MENU_BUTTON_COLOR || this.amoTheme.mainMenuButtonColor,
          mainMenuButtonBorderColor: parameters.MAIN_MENU_BUTTON_BORDER_COLOR || this.amoTheme.mainMenuButtonBorderColor,
          mainMenuButtonFontColor: parameters.MAIN_MENU_BUTTON_FONT_COLOR || this.amoTheme.mainMenuButtonFontColor,
          mainMenuButtonFontSize: this.normalizeCssSize(parameters.MAIN_MENU_BUTTON_FONT_SIZE, this.amoTheme.mainMenuButtonFontSize)
        });
      } catch (e) {
        // Keep the AMO defaults when Strapi theme parameters are unavailable.
      }

      try {
        const response = await axios.get(`${base}/api/parameter-images?populate=*`);
        const rawImages = response && response.data && Array.isArray(response.data.data) ? response.data.data : [];
        const banner = rawImages.find((item) => {
          const attributes = item && item.attributes ? item.attributes : {};
          return attributes.Name === 'TOP_PAGE_BANNER_LOGO';
        });
        const imagePath = banner
          && banner.attributes
          && banner.attributes.Image
          && banner.attributes.Image.data
          && banner.attributes.Image.data.attributes
          && banner.attributes.Image.data.attributes.url;

        if (imagePath) {
          this.amoTheme = Object.assign({}, this.amoTheme, {
            topPageBannerLogo: this.resolveStrapiAssetUrl(base, imagePath)
          });
        }
      } catch (e) {
        // The store logo remains the fallback image.
      }
    },
    async loadStrapiMenu() {
      if (!this.isAmoSite) {
        this.strapiMenu = [];
        return;
      }

      const strapiBaseUrl = this.getStrapiBaseUrl();
      if (!strapiBaseUrl) {
        this.strapiMenu = [];
        return;
      }

      try {
        const base = String(strapiBaseUrl).replace(/\/$/, '');
        const response = await axios.get(`${base}/api/menus?populate=*&pagination[pageSize]=1000&locale=${encodeURIComponent(this.strapiLocale)}`);
        const rawMenu = response && response.data && Array.isArray(response.data.data) ? response.data.data : [];
        this.strapiMenu = rawMenu
          .slice()
          .sort((a, b) => this.sortValue(a) - this.sortValue(b))
          .map((item, index) => this.normalizeMenuItem(item, index))
          .filter(Boolean);
      } catch (e) {
        this.strapiMenu = [];
      }
    },
    normalizeMenuItem(item, index) {
      const attributes = item && item.attributes ? item.attributes : {};
      const hideOnTopMenu = attributes.bHideOnTopMenu === true || attributes.bHideOnTopMenu === 'true' || attributes.bHideOnTopMenu === 1;
      if (attributes.Visible === false || hideOnTopMenu) return null;

      const children = this.normalizeSubMenus(attributes.sub_menus, item.id || index);
      return {
        id: `menu-${item.id || index}`,
        name: attributes.name || attributes.Name || '',
        href: this.resolveMenuHref(attributes.Link || attributes.page_path || ''),
        openInNewTab: attributes.IsOpenInNewTab === true,
        children
      };
    },
    normalizeSubMenus(subMenus, parentId) {
      const data = subMenus && Array.isArray(subMenus.data) ? subMenus.data : [];
      return data
        .slice()
        .sort((a, b) => this.sortValue(a, true) - this.sortValue(b, true))
        .map((item, index) => this.normalizeSubMenuItem(item, parentId, index))
        .filter(Boolean);
    },
    normalizeSubMenuItem(item, parentId, index) {
      const attributes = item && item.attributes ? item.attributes : {};
      if (attributes.Visible === false) return null;

      return {
        id: `submenu-${parentId}-${item.id || index}`,
        name: attributes.Name || attributes.name || '',
        href: this.resolveMenuHref(attributes.Link || attributes.page_path || ''),
        openInNewTab: attributes.IsOpenInNewTab === true
      };
    },
    sortValue(item, useSubMenuOrder = false) {
      const attributes = item && item.attributes ? item.attributes : {};
      const raw = useSubMenuOrder
        ? attributes.Sort_Order || attributes.sort_order
        : attributes.sort_order || attributes.Sort_Order;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : 9999;
    },
    resolveMenuHref(link) {
      const value = String(link || '').trim();
      if (!value) return this.logoHomeHref;
      if (/^(#|mailto:|tel:|javascript:)/i.test(value)) return value;
      if (typeof window === 'undefined') return value;

      try {
        const origin = window.location.origin;
        const url = new URL(value, origin);
        const normalizedPath = url.pathname.replace(/\/+$/, '').toLowerCase();
        if (url.origin === origin && normalizedPath === '/ll/login') {
          return `${origin}/participant/#/login`;
        }
        return url.href;
      } catch (e) {
        return value;
      }
    }
  },
  async created() {
    const env = await this.waitForEnv();
    this.marketingRoot = this.storeSitesAppRoot || (typeof window !== 'undefined' ? window.location.origin : '');
    this.livelapsHost = (env && env.get && env.get('VUE_APP_LIVELAPS_HOST')) || '';
    const bg = env && env.get && env.get('VUE_APP_HEADER_COLOR');
    if (bg) this.headerBg = bg;
    this.envReady = true;
    await this.loadAmoTheme();
    await this.loadStrapiMenu();
  }
};
</script>

<style scoped>
.ll-pub-hdr { width:100%; box-sizing:border-box; font-family:'Roboto', sans-serif; }

.ll-amo-header {
  position:relative;
  z-index:50;
  overflow:visible;
}
.ll-amo-top {
  position:relative;
  display:flex;
  align-items:center;
  justify-content:center;
  min-height:112px;
  height:112px;
  padding:0 72px;
  box-sizing:border-box;
}
.ll-amo-logo-link {
  display:flex;
  align-items:center;
  justify-content:center;
  width:360px;
  max-width:60vw;
  height:100%;
  text-decoration:none;
}
.ll-amo-logo-img {
  display:block;
  max-width:100%;
  max-height:96px;
  object-fit:contain;
}
.ll-amo-icon-btn,
.ll-amo-lang {
  position:absolute;
  top:40px;
  display:flex;
  align-items:center;
  justify-content:center;
  width:42px;
  height:34px;
  border:1px solid;
  border-radius:4px;
  box-sizing:border-box;
  color:inherit;
}
.ll-amo-icon-btn {
  padding:0;
  cursor:pointer;
}
.ll-amo-menu-toggle { left:16px; }
.ll-amo-lang { right:6px; }
.ll-amo-icon {
  font-size:13px;
  line-height:1;
  text-transform:none !important;
}
.ll-amo-menu-row {
  display:flex;
  align-items:stretch;
  justify-content:center;
  min-height:var(--amo-menu-button-height, 30px);
  height:var(--amo-menu-button-height, 30px);
  overflow:visible;
  -webkit-overflow-scrolling:touch;
}
.ll-amo-menu-item {
  display:inline-flex;
  align-items:center;
  justify-content:center;
  min-height:100%;
  height:100%;
  padding:0 18px;
  border:1px solid;
  border-radius:0;
  color:inherit !important;
  font-weight:700;
  line-height:1.2;
  letter-spacing:0;
  text-transform:uppercase;
  text-decoration:none !important;
  white-space:nowrap;
  cursor:pointer;
  box-sizing:border-box;
}
.ll-amo-menu-item:hover {
  box-shadow:inset 0 0 0 999px rgba(255,255,255,.08);
}
.ll-amo-menu-parent {
  gap:6px;
}
.ll-amo-caret {
  font-size:10px;
  line-height:1;
  text-transform:none !important;
}
.ll-amo-dd {
  position:relative;
  display:inline-flex;
  align-items:stretch;
  min-height:100%;
}
.ll-amo-dd-menu {
  position:absolute;
  top:100%;
  left:0;
  z-index:240;
  min-width:210px;
  padding:0;
  border:1px solid rgba(0,0,0,.18);
  box-shadow:0 6px 12px rgba(0,0,0,.18);
}
.ll-amo-dd-item {
  display:flex;
  align-items:center;
  min-height:34px;
  padding:8px 14px;
  border:1px solid;
  border-width:0 0 1px;
  color:inherit !important;
  font-weight:700;
  letter-spacing:0;
  text-transform:uppercase;
  text-decoration:none !important;
  white-space:nowrap;
}
.ll-amo-dd-item:last-child { border-bottom-width:0; }
.ll-amo-dd-item:hover { box-shadow:inset 0 0 0 999px rgba(255,255,255,.08); }
.ll-amo-overlay { z-index:210; }
.ll-amo-drawer { z-index:220; }
.ll-amo-lang ::v-deep .v-btn {
  width:40px !important;
  height:32px !important;
  min-width:40px !important;
  padding:0 !important;
  border:none !important;
  background:transparent !important;
  color:inherit !important;
}
.ll-amo-lang ::v-deep .v-btn .v-icon {
  color:inherit !important;
  font-size:13px !important;
}
.ll-amo-lang ::v-deep .v-btn .v-icon--small,
.ll-amo-lang ::v-deep .v-btn .txt {
  display:none !important;
}

@media (max-width: 960px) {
  .ll-amo-top {
    min-height:88px;
    height:88px;
    padding:0 58px;
  }
  .ll-amo-logo-link {
    width:300px;
    max-width:62vw;
  }
  .ll-amo-logo-img { max-height:72px; }
  .ll-amo-icon-btn,
  .ll-amo-lang {
    top:27px;
    width:38px;
    height:34px;
  }
  .ll-amo-menu-toggle { left:12px; }
  .ll-amo-lang { right:6px; }
  .ll-amo-menu-row {
    justify-content:flex-start;
    overflow-x:auto;
    overflow-y:hidden;
  }
  .ll-amo-menu-item {
    padding:0 14px;
  }
}

@media (max-width: 560px) {
  .ll-amo-logo-link {
    width:240px;
    max-width:58vw;
  }
  .ll-amo-logo-img { max-height:60px; }
  .ll-amo-menu-item { padding:0 12px; }
}

.ll-logo-link { display:inline-flex; align-items:center; text-decoration:none; flex-shrink:0; margin-right:30px; }
.ll-logo-img { height:40px; max-width:180px; }

.ll-desk { display:flex; align-items:center; justify-content:center; max-width:1185px; margin:0 auto; padding:5px 12px; }
.ll-desk-nav { display:flex; align-items:center; flex-wrap:nowrap; gap:6px; min-width:0; }
.ll-desk-lang { flex-shrink:0; color:#fff; margin-left:6px; }
.ll-desk-lang ::v-deep .v-btn {
  border:thin solid rgba(255,255,255,.65) !important; border-radius:4px !important;
  height:32px !important; width:auto !important; padding:0 8px !important;
  background:transparent !important;
}
.ll-desk-lang ::v-deep .v-btn .v-icon { color:#fff !important; font-size:14px !important; }
.ll-desk-lang ::v-deep .v-btn .v-icon--small { font-size:10px !important; margin-left:2px; }
.ll-desk-lang ::v-deep .v-btn .txt { color:#fff !important; display:inline !important; font-size:11px; font-weight:500; letter-spacing:.09em; text-transform:uppercase; margin-left:6px; }

.ll-desk-btn {
  display:inline-flex; align-items:center; gap:6px;
  padding:0 8px; height:32px;
  border:thin solid rgba(255,255,255,.65); border-radius:4px;
  color:#fff !important; text-decoration:none !important;
  font-size:11px; font-weight:500; letter-spacing:.09em;
  white-space:nowrap; background:transparent; cursor:pointer;
  transition:background .15s;
}
.ll-desk-btn:hover { background:rgba(255,255,255,.12); }
.ll-fa { font-size:14px !important; text-transform:none !important; }
.ll-caret { font-size:10px; margin-left:2px; }

.ll-desk-dd { position:relative; }
.ll-desk-dd-menu {
  position:absolute; top:100%; left:0; z-index:1000;
  min-width:200px; padding:5px 0; margin-top:2px;
  background:#fff; border:1px solid rgba(0,0,0,.15); border-radius:4px;
  box-shadow:0 6px 12px rgba(0,0,0,.175);
}
.ll-desk-dd-item {
  display:flex; align-items:center; gap:8px;
  padding:6px 20px; color:#333 !important; text-decoration:none !important;
  font-size:13px; font-weight:400; white-space:nowrap; cursor:pointer;
  min-height:36px;
}
.ll-desk-dd-item:hover { background:#f5f5f5; }
.ll-dd-icon { width:1rem; text-align:center; color:#555; font-size:.85rem; }

@media (max-width: 1500px) {
  .ll-desk { max-width:90%; }
}
@media (max-width: 1264px) {
  .ll-desk-btn { padding:0 6px; font-size:10px; }
}

.ll-mob-lang-inline { color:#fff; }
.ll-mob-lang-inline ::v-deep .v-btn { height:32px !important; width:auto !important; color:#fff !important; }
.ll-mob-lang-inline ::v-deep .v-btn .v-icon { color:#fff !important; font-size:14px !important; }
.ll-mob-lang-inline ::v-deep .v-btn .txt { color:#fff !important; display:inline !important; font-size:13px; text-transform:uppercase; }

.ll-mob { width:100%; padding:5px 12px; }
.ll-mob-topbar { display:flex; align-items:center; justify-content:space-between; }
.ll-hamburger {
  display:flex; align-items:center; justify-content:center;
  width:38px; height:38px; border:1px solid rgba(255,255,255,.85); border-radius:4px;
  background:transparent; color:#fff; font-size:1.1rem; cursor:pointer;
}
.ll-hamburger-icon { text-transform:none !important; }

.ll-mob-overlay { position:fixed; inset:0; background:rgba(0,0,0,.35); z-index:199; }
.ll-mob-drawer {
  position:fixed; top:0; left:0; bottom:0; width:300px; max-width:85vw;
  background:#fff; z-index:200; overflow-y:auto; -webkit-overflow-scrolling:touch;
  box-shadow:4px 0 20px rgba(0,0,0,.18);
}
.ll-mob-drawer-inner { padding:12px 0 20px; }

.ll-slide-enter-active, .ll-slide-leave-active { transition:transform .25s ease; }
.ll-slide-enter, .ll-slide-leave-to { transform:translateX(-100%); }

.ll-mob-link {
  display:flex; align-items:center; gap:14px; width:100%;
  padding:14px 20px; border:none; border-bottom:1px solid rgba(0,0,0,.08);
  background:transparent; color:#333 !important; text-decoration:none !important;
  font-size:15px; font-weight:600; text-align:left; cursor:pointer;
}
.ll-mob-fa { width:1.25rem; text-align:center; color:#555; font-size:1rem; flex-shrink:0; text-transform:none !important; }

.ll-mob-toggle { justify-content:space-between; }
.ll-mob-toggle-left { display:flex; align-items:center; gap:14px; }
.ll-mob-chevron { font-size:.7rem; color:#888; flex-shrink:0; text-transform:none !important; }

.ll-mob-sub { padding:0 20px 8px 56px; }
.ll-mob-sublink {
  display:block; padding:10px 0;
  color:#444 !important; text-decoration:none !important;
  font-size:14px; font-weight:500; text-align:left;
  border-bottom:1px solid rgba(0,0,0,.06);
}
.ll-mob-sublink:last-child { border-bottom:none; }

.ll-mob-lang-group { padding:12px 20px; border-bottom:none; }
.ll-mob-lang-group ::v-deep .txt { color:#333 !important; display:inline !important; font-size:15px; font-weight:600; text-transform:uppercase; }
.ll-mob-lang-group ::v-deep .v-btn { height:auto !important; width:auto !important; }
.ll-mob-lang-group ::v-deep .v-btn .v-icon { color:#555 !important; font-size:1rem !important; }
.ll-mob-lang-group ::v-deep .v-btn .v-icon--small { color:#888 !important; font-size:.7rem !important; }
</style>
